import React, { useState } from 'react';
import { CommunityIdea, CommunityStrategy, CommunityComment } from 'types/ApiServicesTypes';
import Comment from './Comment';
import { useGetCommunityComments, useCreateCommunityComment } from 'services/Community';
import { Box, TextField, Button, Typography } from '@mui/material';

interface CommentSectionProps {
  post: CommunityIdea | CommunityStrategy;
}

const CommentSection: React.FC<CommentSectionProps> = ({ post }) => {
  const [commentText, setCommentText] = useState('');

  // Determine if this is an idea or strategy and get the ID
  const ideaId = (post as CommunityIdea).likes_count !== undefined ? post.id : undefined;
  const strategyId = (post as CommunityStrategy).likes_count !== undefined ? undefined : post.id;

  // API Hooks
  const commentsQuery = useGetCommunityComments(1, 50, ideaId, strategyId);
  const createCommentMutation = useCreateCommunityComment();

  const handleAddComment = () => {
    if (!commentText.trim()) return;
    
    createCommentMutation.mutate({
      params: { content: commentText },
      ideaId,
      strategyId,
    }, {
      onSuccess: () => {
        setCommentText('');
      }
    });
  };

  const comments = commentsQuery.data?.data || [];
  const isLoading = commentsQuery.isLoading;

  return (
    <Box sx={{ marginTop: 4 }}>
      <Typography variant="h6" sx={{ marginBottom: 2 }}>
        Comments ({comments.length})
      </Typography>
      
      {isLoading ? (
        <Box sx={{ textAlign: 'center', py: 2 }}>
          <Typography color="text.secondary">Loading comments...</Typography>
        </Box>
      ) : comments.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 2 }}>
          <Typography color="text.secondary">No comments yet. Be the first!</Typography>
        </Box>
      ) : (
        <Box sx={{ marginBottom: 3 }}>
          {comments.map(comment => (
            <Comment key={comment.id} comment={comment} />
          ))}
        </Box>
      )}
      
      <Box sx={{ marginTop: 2 }}>
        <TextField
          fullWidth
          multiline
          rows={3}
          value={commentText}
          onChange={(e) => setCommentText(e.target.value)}
          placeholder="Add a comment..."
          variant="outlined"
          disabled={createCommentMutation.isLoading}
          sx={{ marginBottom: 1 }}
        />
        <Button
          variant="contained"
          onClick={handleAddComment}
          disabled={!commentText.trim() || createCommentMutation.isLoading}
          sx={{ minWidth: 120 }}
        >
          {createCommentMutation.isLoading ? 'Posting...' : 'Post Comment'}
        </Button>
      </Box>
    </Box>
  );
};

export default CommentSection; 